"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getWebsocketUrl = exports.getFeatureFlag = exports.iterableToArray = exports.getFilesForTransfer = exports.folderTransferSpecExplode = exports.parseFilesFromSpec = exports.isValidForm = exports.checkChildrenForFiles = exports.isValidTransferSpec = exports.isValidUrl = exports.generateErrorBody = exports.errorLog = exports.generatePromiseObjects = void 0;
var index_1 = require("../index");
var constants_1 = require("../constants/constants");
var messages_1 = require("../constants/messages");
/**
 * Generates promise object that can be resolved or rejected via functions
 *
 * @returns an object containing the promise, the resolver and rejecter
 */
var generatePromiseObjects = function () {
    var resolver;
    var rejecter;
    var promise = new Promise(function (resolve, reject) {
        resolver = resolve;
        rejecter = reject;
    });
    return {
        promise: promise,
        resolver: resolver,
        rejecter: rejecter
    };
};
exports.generatePromiseObjects = generatePromiseObjects;
/**
 * Log errors from HTTP Gateway SDK
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 */
var errorLog = function (message, debugData) {
    if (typeof window === 'object') {
        if (!Array.isArray(window.httpGatewayLogs)) {
            window.httpGatewayLogs = [];
        }
        window.httpGatewayLogs.push({ message: message, debugData: debugData });
    }
    console.warn("Aspera HTTP Gateway SDK: ".concat(message), debugData);
};
exports.errorLog = errorLog;
/**
 * Generate error object for rejecter responses
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 *
 * @returns object containing standardized error response
 */
var generateErrorBody = function (message, debugData) {
    var errorResponse = {
        error: true,
        message: message
    };
    if (debugData) {
        errorResponse.debugData = debugData;
    }
    return errorResponse;
};
exports.generateErrorBody = generateErrorBody;
/**
 * Validate if URL is valid URL
 *
 * @param serverUrl the url to test
 *
 * @returns boolean indicating whether supplied url is valid
 */
var isValidUrl = function (url) {
    if (url &&
        typeof url === 'string' &&
        url.indexOf('//') > -1) {
        return true;
    }
    return false;
};
exports.isValidUrl = isValidUrl;
/**
 * Validate if transferSpec is valid for server communication
 *
 * @param transferSpec the transferSpec to test
 *
 * @returns boolean indicating whether supplied transferSpec is valid
 */
var isValidTransferSpec = function (transferSpec) {
    if (transferSpec &&
        typeof transferSpec === 'object' &&
        typeof transferSpec.direction === 'string' &&
        typeof transferSpec.remote_host === 'string' &&
        Array.isArray(transferSpec.paths)) {
        return true;
    }
    return false;
};
exports.isValidTransferSpec = isValidTransferSpec;
/**
 * Get files from a form
 *
 * @param children form children list
 *
 * @returns an array of Files pulled from an HTML form
 */
var checkChildrenForFiles = function (children) {
    var filesFromForm = [];
    children.forEach(function (child) {
        if (child.files) {
            for (var i = 0; i < child.files.length; i++) {
                filesFromForm.push(child.files[i]);
            }
        }
    });
    return filesFromForm;
};
exports.checkChildrenForFiles = checkChildrenForFiles;
/**
 * Validate if form has required items
 *
 * @param form HTML form element
 */
var isValidForm = function (form) {
    if (form &&
        typeof form === 'object' &&
        form.nodeType &&
        form.tagName === 'FORM') {
        return true;
    }
    return false;
};
exports.isValidForm = isValidForm;
/**
 * Take a list of files and match them to a provided transferSpec to find only files to use
 *
 * @param files list of files in JavaScript memory
 * @param transferSpec the transferSpec to compare to for what files to use
 */
var parseFilesFromSpec = function (files, transferSpec) {
    var transferSpecFilesMap = new Map();
    var filesAddedList = new Map();
    transferSpec.paths.forEach(function (path) {
        transferSpecFilesMap.set(path.source, path);
    });
    return files.filter(function (file) {
        if (filesAddedList.get(file.name)) {
            return false;
        }
        var specPath = transferSpecFilesMap.get(file.name);
        if (specPath) {
            specPath.file_size = file.size;
            filesAddedList.set(file.name, true);
        }
        return !!specPath;
    });
};
exports.parseFilesFromSpec = parseFilesFromSpec;
/**
 * Take a transferSpec and find folders being used in the form and explode for uploading and explode transferSpec to know all files in folder
 *
 * @param formId the form item to use for parsing
 * @param transferSpec he transfer spec to test with for file list
 *
 * @returns object with files to add to upload and a new transferSpec Path array to use with exploded files
 */
var folderTransferSpecExplode = function (formId, transferSpec) {
    var _a;
    var folderList = index_1.asperaHttpGateway.globals.getOrCreateFolderDataList(formId);
    var folderDropList = index_1.asperaHttpGateway.globals.getOrCreateFolderDataList(constants_1.dropContainerName);
    var newPaths = [];
    var foundFiles = [];
    var handleFolderData = function (file, path) {
        foundFiles.push(file);
        newPaths.push({
            source: file.asperaDropPath || file.webkitRelativePath || file.name,
            destination: path.destination,
        });
    };
    (_a = transferSpec.paths) === null || _a === void 0 ? void 0 : _a.forEach(function (path) {
        if (folderList.get(path.source)) {
            folderList.get(path.source).files.forEach(function (file) {
                handleFolderData(file, path);
            });
        }
        else if (folderDropList.get(path.source)) {
            folderDropList.get(path.source).files.forEach(function (file) {
                handleFolderData(file, path);
            });
        }
        else {
            newPaths.push(path);
        }
    });
    return {
        files: foundFiles,
        newPath: newPaths,
    };
};
exports.folderTransferSpecExplode = folderTransferSpecExplode;
/**
 * Return array of File items to use for transfer
 *
 * @param formId the form item to use for parsing
 * @param transferSpec the transfer spec to test with for file list
 *
 * @returns the array of Files to use
 */
var getFilesForTransfer = function (formId, transferSpec) {
    var form = index_1.asperaHttpGateway.globals.createOrUseForm(formId);
    if (!(0, exports.isValidForm)(form)) {
        return [];
    }
    var filesFromForm = (0, exports.checkChildrenForFiles)(form.childNodes);
    var dropFiles = index_1.asperaHttpGateway.globals.getOrCreateFormFileListFromDrop(constants_1.dropContainerName);
    dropFiles.forEach(function (file) {
        filesFromForm.push(file);
    });
    return (0, exports.parseFilesFromSpec)(filesFromForm, transferSpec);
};
exports.getFilesForTransfer = getFilesForTransfer;
/**
 * Convert an irritable into an array. This is useful for taking Maps and making Arrays (Map.values() Map.keys())
 *
 * @param iterable - an iterable to take and convert to an array
 *
 * @returns an array containing the iterable values
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var iterableToArray = function (iterable) {
    var newArray = [];
    (function addItem() {
        var value = iterable.next();
        if (!value.done) {
            newArray.push(value.value);
            addItem();
        }
    }());
    return newArray;
};
exports.iterableToArray = iterableToArray;
/**
 * Figure out which feature to use based on current version of system.
 *
 * @param featureFlag - name of the feature flag to test
 *
 * @returns indicator if feature should be used
 */
var getFeatureFlag = function (featureFlag) {
    var _a;
    if (!((_a = index_1.asperaHttpGateway.globals.serverInfo) === null || _a === void 0 ? void 0 : _a.version)) {
        (0, exports.errorLog)(messages_1.messages.serverNotVerified);
        return false;
    }
    var versionData = index_1.asperaHttpGateway.globals.serverInfo.version.split('.');
    var major = versionData[0] ? Number(versionData[0]) : 0;
    var minor = versionData[1] ? Number(versionData[1]) : 0;
    var patch = versionData[2] ? Number(versionData[2]) : 0;
    var numberBased = (major * 10000000000) + (minor * 100000) + patch;
    switch (featureFlag) {
        case 'noBase64Encoding':
            return numberBased >= 20000300000;
    }
};
exports.getFeatureFlag = getFeatureFlag;
/**
 * Returns a string indicating the websocket URL to use for talking to the server
 *
 * @returns a string of the full Websocket URL
 */
var getWebsocketUrl = function (serverUrl, protocol) {
    var wsProtocol;
    if (serverUrl.indexOf('http:') === 0) {
        wsProtocol = 'ws';
    }
    else if (serverUrl.indexOf('https:') === 0) {
        wsProtocol = 'wss';
    }
    else {
        wsProtocol = protocol === 'https:' ? 'wss' : 'ws';
    }
    var url = serverUrl.replace('http://', '//').replace('https://', '//');
    return "".concat(wsProtocol, ":").concat(url, "/").concat((0, exports.getFeatureFlag)('noBase64Encoding') ? 'v2' : 'v1', "/upload");
};
exports.getWebsocketUrl = getWebsocketUrl;
exports.default = {
    generatePromiseObjects: exports.generatePromiseObjects,
    errorLog: exports.errorLog,
    generateErrorBody: exports.generateErrorBody,
    isValidUrl: exports.isValidUrl,
    isValidTransferSpec: exports.isValidTransferSpec,
    checkChildrenForFiles: exports.checkChildrenForFiles,
    isValidForm: exports.isValidForm,
    getFilesForTransfer: exports.getFilesForTransfer,
    folderTransferSpecExplode: exports.folderTransferSpecExplode,
    parseFilesFromSpec: exports.parseFilesFromSpec,
    iterableToArray: exports.iterableToArray,
    getFeatureFlag: exports.getFeatureFlag,
};
